@if (!isStagedAcquisition) {
  <div class="acquisition-header">
    <mat-checkbox
      test-id="acquisition-checkbox"
      [attr.data-id]="'acquisitions-select-checkbox'"
      [checked]="checked()"
      (change)="checkedChange.emit($event.checked)"
    />
    <span class="acquisition-info">
      <span class="acquisition-label" data-id="acquisitions-name-label">{{ acquisition().label }}</span>
      <span class="acquisition-timestamp">{{ timestamp }}</span>
    </span>

    <fw-icon-button
      color="primary"
      icon="menu-vertical"
      size="small"
      test-id="acquisition-options"
      [attr.data-id]="'acquisitions-options-dropdown'"
      [fwMenuTriggerFor]="menu"
    />
    <ng-template #menu>
      <fw-menu-container>
        <fw-menu>
          @if (permissions().viewMetadata) {
            <fw-menu-item test-id="acquisition-info-menu-item" title="Information" (click)="information()" />
          }
          @if (permissions().downloadFiles) {
            <fw-menu-item
              test-id="acquisition-download-files-menu-item"
              title="Download Acquisition Files"
              (click)="downloadFiles()"
            />
          }
          @if (permissions().deleteContainer) {
            <fw-menu-item test-id="acquisition-delete-menu-item" title="Delete Acquisition" (click)="delete()" />
          }
          @if (permissions().uploadFiles) {
            <fw-menu-item
              test-id="acquisition-upload-data-menu-item"
              title="Upload Data to Acquisition"
              (click)="uploadFiles()"
            />
            <fw-menu-item test-id="acquisition-create-file-menu-item" title="Create New File" (click)="createFile()" />
          }
          @if (permissions().viewNotes) {
            <fw-menu-item test-id="acquisition-notes-menu-item" title="Notes" (click)="notes()" />
          }
          @if (permissions().tasksModify && features.tasksRefactor) {
            <fw-menu-separator />
            <fw-menu-item
              test-id="acquisition-create-reader-task-menu-item"
              title="Create Reader Task"
              [description]="isProjectLocked() ? 'Project is locked' : null"
              [disabled]="isProjectLocked() || !hasDicomFiles()"
              [fwTooltip]="hasDicomFiles() ? null : 'At least one DICOM file is required'"
              (click)="!isProjectLocked() && createReaderTask()"
            />
          }
          @if (permissions().viewMetadata) {
            @if (viewerApps().length) {
              <fw-menu-separator />
            }
            @for (app of viewerApps(); track app) {
              <fw-menu-item
                test-id="acquisition-launch-in-menu-item"
                title="Launch in {{ app.name }}"
                (click)="viewAcquisition(app)"
              />
            }
          }
        </fw-menu>
      </fw-menu-container>
    </ng-template>
  </div>
}

<!-- Form to create a new acquisition with staged files -->
@if (isStagedAcquisition) {
  <div class="acquisition-header staged-acquisition" [formGroup]="stagingForm">
    <span class="acquisition-info">
      <mat-form-field appearance="outline" class="solid-background">
        <input
          formControlName="label"
          matInput
          placeholder="New acquisition label"
          test-id="acquisition-label-input"
          (keydown.enter)="saveStagedAcquisition()"
        />
      </mat-form-field>
    </span>
    <span class="acquisition-buttons">
      <button
        color="primary"
        mat-flat-button
        test-id="save-staged-acquisition"
        [disabled]="!stagingForm.valid"
        (click)="saveStagedAcquisition()"
      >
        Save
      </button>
      <button mat-stroked-button test-id="cancel-staged-acquisition" (click)="cancelStagedAcquisition()">Cancel</button>
    </span>
  </div>
}

@if (acquisition().files.length === 0) {
  <div class="file-row">
    <span class="left-icons-wrapper"></span>
    <span>There are no files in this acquisition.</span>
  </div>
}

@for (file of acquisitionFiles(); track file.file_id) {
  <div
    #fileRow
    cdkDrag
    class="file-row"
    [cdkDragData]="{ file: file, acquisition: acquisition }"
    [cdkDragDisabled]="disableFileDrag() || !!fileRenames()[file.file_id]"
    [ngClass]="{
      disabled: isQuarantined(file),
      'editable-filename': permissions().renameFiles,
      'editable-filetype': permissions().modifyFileMetadata,
      selected: (state.fileSelection$ | async)?.has(file.file_id),
      invisible: (state.fileDragging$ | async) && (state.fileSelection$ | async)?.has(file.file_id),
    }"
    (cdkDragEnded)="state.fileDragging$.next(null)"
    (cdkDragStarted)="state.fileDragging$.next(file)"
    (click)="selectFile($event, file)"
  >
    <div *cdkDragPreview>
      @if ((state.fileSelection$ | async)?.size; as selectionCount) {
        <span class="badge-icon">{{ selectionCount }}</span>
      }
      {{ file.name }}
    </div>
    <div *cdkDragPlaceholder>Move files to this acquisition</div>
    <span class="left-icons-wrapper">
      @if (getFileOrigin(file); as originInfo) {
        @if (originInfo.matIcon) {
          <mat-icon class="xs" [fwTooltip]="originInfo.popoverText">
            {{ originInfo.matIcon }}
          </mat-icon>
        }
      }
      @if (file.copy_of) {
        <mat-icon class="xs" fwTooltip="This file is referenced from {{ file.copy_of.project_label }}.">link</mat-icon>
      }
    </span>
    <div class="file-name" data-id="acquisitions-file-label">
      @if (!fileRenames()[file.file_id]) {
        <fw-ellipsis test-id="analysis-input-file-name" [text]="file.name" (click)="startFileRename(file)" />
      }
      @if (file.version > 1 && !fileRenames()[file.file_id]) {
        <file-version-badge class="version-badge" [file]="file" />
      }
      @if (fileRenames()[file.file_id]) {
        <div class="file-name-input">
          <fw-text-input
            autocomplete="false"
            size="small"
            test-id="file-name-input"
            [errorInIconTooltip]="true"
            [errorText]="getErrorMessage(file.file_id)"
            [formControl]="fileRenames()[file.file_id]"
            (keydown.enter)="saveFileRename(file)"
          />
          <fw-icon-button
            fwTooltip="Save"
            icon="done-check"
            size="small"
            test-id="save-filename"
            [disabled]="fileRenames()[file.file_id].invalid || fileRenames()[file.file_id].pristine"
            (click)="saveFileRename(file)"
          />
          <fw-icon-button
            fwTooltip="Close"
            icon="close"
            size="small"
            test-id="cancel-filename"
            (click)="cancelFileRename(file)"
          />
        </div>
      }
    </div>
    <span class="ellipsis file-created" data-id="acquisitions-file-label" [fwTooltip]="file.created">
      {{ file.created | date: 'yyyy-MM-dd hh:mm' }}
    </span>
    <span
      class="ellipsis file-classification"
      data-id="acquisitions-modality-label"
      [fwTooltip]="getFileClassification(file)"
    >
      {{ getFileClassification(file) }}
    </span>
    <div class="file-type-actions">
      @if (typeChanges()[file.file_id]) {
        <fw-select
          maxOptionsHeight="300px"
          size="small"
          test-id="file-type-select"
          width="170px"
          [formControl]="typeChanges()[file.file_id]"
          [options]="fileTypes()"
        />
        <fw-icon-button
          fwTooltip="Save"
          icon="done-check"
          size="small"
          test-id="save-filetype"
          (click)="saveFileTypeSelection(file)"
        />
        <fw-icon-button
          fwTooltip="Close"
          icon="close"
          size="small"
          test-id="cancel-filename"
          (click)="toggleTypeSelect(file)"
        />
      } @else {
        <div #fileTypeContainer class="file-type-container">
          <span class="file-type" data-id="acquisitions-type-label" (click)="toggleTypeSelect(file)">{{
            file.type
          }}</span>
          @if (showCvatIcon(file)) {
            <mat-icon
              [fwTooltip]="getCvatTooltip(file)"
              [ngClass]="[CVATSyncStateClassMap[file?.cvat?.sync_state] || '', 'xs']"
            >
              {{ CVATSyncStateIconMap[file?.cvat?.sync_state] }}
            </mat-icon>
          }
        </div>
        @if (acquisition()?._id && !isQuarantined(file)) {
          @defer (on hover(fileRow)) {
            <file-actions
              #fileActions
              [container]="acquisition()"
              [file]="file"
              [hoverFeatures]="['annotations', 'view', 'info', 'download']"
              [kabobFeatures]="[
                'info',
                'download',
                'delete',
                'version-history',
                'copy-filename',
                'copy-modality',
                'view',
                'tasks',
              ]"
              [viewParams]="fileViewParameters()"
              (afterChange)="state.fileChanges$.next($event)"
              (afterDelete)="state.fileChanges$.next($event)"
            >
              <fw-icon-button
                action="annotations"
                fwTooltipPosition="below"
                icon="pencil-edit-create"
                size="small"
                testId="acquisitions-file-annotations"
                [fwTooltip]="features.segmentationsV3 ? 'Overlays Quick View' : 'Annotations Quick View'"
              />
              <fw-icon-button
                action="cvat"
                color="primary"
                data-id="acquisitions-file-cvat"
                fwTooltip="Launch in CVAT"
                icon="video-player-controls"
                size="small"
                testId="acquisitions-file-cvat"
              />
              <fw-icon-button
                action="view"
                color="primary"
                data-id="acquisitions-file-view"
                fwTooltip="Launch in Viewer"
                icon="image-picture-square"
                size="small"
                testId="acquisitions-file-view"
              />
              <fw-icon-button
                action="info"
                color="primary"
                data-id="acquisitions-file-info"
                fwTooltip="Information"
                icon="information-circle"
                size="small"
                testId="acquisitions-file-info"
              />
              <fw-icon-button
                action="download"
                color="primary"
                data-id="acquisitions-file-download"
                fwTooltip="Download"
                icon="download"
                size="small"
                testId="acquisitions-file-download"
              />
              <fw-icon-button
                color="primary"
                data-id="acquisitions-options-dropdown"
                icon="menu-vertical"
                size="small"
                test-id="file-options"
              />
            </file-actions>
          }
        }
      }
    </div>
  </div>
}
